import { ColumnDef } from '@tanstack/react-table';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { LinkCell } from '../../../../../components/tables/cellComponents/LinkCell';
import YesNoCell from '../../../../../components/tables/cellComponents/YesNoCell';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useToast } from '../../../../../context';
import { useListFiltersInUrl } from '../../../../../hooks';
import { useGetAllLocationsQuery } from '../../../../../services/endpoints/places/location';
import { getCurrentUser } from '../../../../../services/helper';
import { getFilterValue } from '../../../../../utils/listFilterSearchParams';
import {Forbidden} from "../../../../../components/error/Forbidden";
import { canCreateLocation, canEditLocation, canViewLocations } from '../../../../../utils/rbac';

interface DataRow {
  id: number;
  title: string;
  business_entity_name: string;
  primary_city: string;
  manager?: {
    id: number;
    full_name: string;
  };
  invoicing_email: string;
  phone: string;
  hotline: string;
  scheduling_email: string;
  tax_id_number: string;
  is_active: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const LocationsView = () => {
  const { addToast } = useToast();
  const currentUser = getCurrentUser();
  const navigate = useNavigate();

  const [filters, setFilters] = useListFiltersInUrl(
    { scope: 'all', data: { page: 1, entries: 20 } },
    { omitScopeFromUrl: true }
  );
  const { data, error, isLoading, isFetching } = useGetAllLocationsQuery(filters, {
    skip: !canViewLocations(currentUser),
    refetchOnMountOrArgChange: true
  });

  const filterFormFields: FilterField[] = [
    {
      name: 'title',
      label: 'Title',
      value: getFilterValue(filters.data?.filterBy, 'title')
    }
  ];

  const columns: ColumnDef<DataRow>[] = [
    {
      header: 'ID',
      accessorKey: 'id',
      cell: ({ row }) => (
          <LinkCell
              content={row.original.id}
              url={`/admin/places/locations/${row.original.id}`}
          />
      )
    },
    {
      header: 'Title',
      accessorKey: 'title',
      cell: ({ row }) => (
          <LinkCell
              content={row.original.title}
              url={`/admin/places/locations/${row.original.id}`}
          />
      )
    },
    {
      header: 'Business Entity Name',
      accessorKey: 'business_entity_name'
    },
    {
      header: 'Primary City',
      accessorKey: 'primary_city'
    },
    {
      header: 'Area Director',
      accessorKey: 'manager.full_name',
      cell: ({ row }) => (
          <LinkCell
              content={row.original.manager?.full_name ?? ''}
              url={`/admin/dash-users/admins/${row.original.manager?.id}`}
          />
      )
    },
    {
      header: 'Invoicing Email',
      accessorKey: 'invoicing_email'
    },
    {
      header: 'Phone Number',
      accessorKey: 'phone'
    },
    {
      header: 'Hotline',
      accessorKey: 'hotline'
    },
    {
      header: 'Scheduling Email',
      accessorKey: 'scheduling_email'
    },
    {
      header: 'Tax ID Number',
      accessorKey: 'tax_id_number'
    },
    {
      header: 'Is Active',
      accessorKey: 'is_active',
      cell: ({ getValue }) => <YesNoCell value={getValue<boolean>()} />
    },
    {
      header: '',
      accessorKey: 'blank',
      enableSorting: false,
      cell: ({ row }) => (
          <>
            <LinkCell
                content="View"
                url={`/admin/places/locations/${row.original.id}`}
            />
            &nbsp; &nbsp;
            {canEditLocation(currentUser) && (
                <LinkCell
                    content="Edit"
                    url={`/admin/places/locations/${row.original.id}/edit`}
                />
            )}
          </>
      )
    }
  ];

  // Handle API errors with better messaging
  useEffect(() => {
    if (error) {
      let errorMessage = 'Error while loading locations';

      // Extract error message from API response
      if ('data' in error && typeof error.data === 'object' && error.data !== null) {
        const apiError = error.data as { message?: string };
        if (apiError.message) {
          errorMessage = apiError.message;
        }
      } else if ('message' in error && typeof error.message === 'string') {
        errorMessage = error.message;
      }

      addToast(errorMessage, 'error');
    }
  }, [error, addToast]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  // Check permissions before rendering
  if (!canViewLocations(currentUser)) {
    return (
        <Forbidden />
    );
  }

  if (isLoading) {
    return <></>;
  }

  return (
      <div className="py-2">
        <SFCTable
            name={'Locations'}
            columns={columns}
            defaultScope={filters.scope ?? 'all'}
            data={data?.results ?? []}
            count={data?.count}
            isLoading={isLoading}
            isFetching={isFetching}
            indexDownloadPath="locations"
            filters={filters?.data?.filterBy}
            page_Size={filters?.data?.entries}
            filterForm={
              <FilterForm
                  fields={filterFormFields}
                  onApplyFilters={setFilterBy}
              />
            }
            newItemEl={
              canCreateLocation(currentUser) ? (
                  <SimpleButton
                      variant="sfc-blueberry"
                      height={40}
                      truncate={false}
                      onClick={() => navigate(`/admin/places/locations/new`)}
                  >
                    New location
                  </SimpleButton>
              ) : undefined
            }
            onFiltersChanged={setFilters}
        />
      </div>
  );
};